import {
  CardContent,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import { ActionText, NotificationBodyText } from 'components/atoms/Text'
import AvatarHeaderCell from 'components/atoms/table/AvartarHeaderCell'
import PresetTableContainer from 'components/atoms/table/PresetTableContainer'
import UserManagementContainer from 'components/molecules/adminDashboard/UserManagementContainer'
import { useState } from 'react'
import SearchInputBox from 'components/atoms/input/SearchInputBox'
import DeactivateUserButton from './DeactivateUserButton'
import DowngradeUserButton from './DowngradeUserButton'
import InviteMoreButton from './InviteMoreButton'
import ImportContactsButton from './ImportContactsButton'
import UserRoleSelectCell from './UserRoleSelectCell'
import { MONARK_CONTACT_EMAIL } from 'utils/constants'
import { isMatchingUser } from 'utils/userSearchHelper'
import {
  UserCohortPathCellHead,
  UserCohortPathCell
} from 'components/molecules/adminDashboard/UserCohortPathCell'
import { useTranslation } from 'react-i18next'
import UserFeaturesSelectCell from './UserFeatureCell'

const LicensedUsersList = ({
  users,
  organization,
  requestError,
  inviteUserCallback,
  isSuperAdminView,
  licenseActive,
  contentContainerProps,
  downgradeUserCallback,
  deleteUserCallback,
  features
}) => {
  const [userFilterText, setUserFilterText] = useState('')
  const { t } = useTranslation()
  return (
    <UserManagementContainer
      titleText={t('company.licensedUsers')}
      requestError={requestError}
      leftPanelComponent={
        <>
          {organization?.licenseCount ? (
            <>
              <Typography variant='body1Small'>
                {t('company.inviteMoreUsersMonark')}
              </Typography>
              <InviteMoreButton
                organizationId={organization.id}
                licensesAvailable={organization.licenseStats.licensesAvailable}
                successCallback={inviteUserCallback}
                isSuperAdminView={isSuperAdminView}
              />
            </>
          ) : null}
          <Typography variant='body1Small'>
            {`${organization?.licenseStats.licensesUsed || 0}/${
              organization?.licenseCount || 0
            } ${t('company.licensesInUse')}`}
          </Typography>
          {licenseActive === false ? (
            <Typography variant='body1Small'>
              {t('company.licensesHaveExpired')}
            </Typography>
          ) : null}
          <Typography variant='body1Small'>
            {licenseActive === false
              ? t('company.wantToReactivateSubscription')
              : t('company.needMoreLicenses')}
            {t('company.contactUs')}
            <ActionText
              href={`mailto:${MONARK_CONTACT_EMAIL}?subject=${t('company.getMoreMonarkLicenses')}`}>
              {MONARK_CONTACT_EMAIL}
            </ActionText>
          </Typography>
          {organization?.id && isSuperAdminView && (
            <ImportContactsButton organizationId={organization.id} />
          )}
        </>
      }>
      {!organization?.licenseType ? (
        <NotificationBodyText>
          {t('company.youDoNotHaveActiveSubscription')}
        </NotificationBodyText>
      ) : organization?.users?.length > 0 ? (
        <Stack {...contentContainerProps}>
          <SearchInputBox
            onChangeHandler={e => setUserFilterText(e.target.value)}
            placeholder={t('overview.searchForUser')}
          />
          <PresetTableContainer
            shorterRow={true}
            maxHeight='500px'
            otherSX={{ '& .MuiTableCell-head': { verticalAlign: 'middle' } }}
            headers={
              <>
                <TableCell>{t('company.userDetails')}</TableCell>
                <TableCell />
                <TableCell>
                  <UserCohortPathCellHead />
                </TableCell>
                <TableCell>{t('company.userRole')}</TableCell>
                {isSuperAdminView && (
                  <TableCell>{t('headers.features')}</TableCell>
                )}
                <TableCell width='100px' />
              </>
            }>
            {Object.entries(users)
              .filter(([, u]) => u.userLicense)
              .filter(([, u]) => isMatchingUser(u, userFilterText))
              .map(([id, u]) => (
                <TableRow key={id}>
                  <AvatarHeaderCell user={u} />
                  <TableCell />
                  <UserCohortPathCell user={u} />
                  <UserRoleSelectCell user={u} disabled={!licenseActive} />
                  {isSuperAdminView && (
                    <UserFeaturesSelectCell user={u} features={features} />
                  )}
                  <TableCell width='100px'>
                    {licenseActive ? (
                      <InCardStack spacing={0} direction='row'>
                        <DowngradeUserButton
                          successCallback={downgradeUserCallback}
                          user={u}
                        />
                        {u.role ? (
                          <DeactivateUserButton
                            successCallback={userId => {
                              downgradeUserCallback(userId)
                              deleteUserCallback(userId)
                            }}
                            user={u}
                            fromLicensed={true}
                          />
                        ) : null}
                      </InCardStack>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
          </PresetTableContainer>
        </Stack>
      ) : (
        <CardContent>
          <NotificationBodyText>
            {t('company.doNotHaveLicensedUsers')}
          </NotificationBodyText>
        </CardContent>
      )}
    </UserManagementContainer>
  )
}

export default LicensedUsersList
